<template>
<div>
  <loading-panel :loading="loading"></loading-panel>

  <!-- Feature not enabled -->
  <card v-if="feature_not_enabled">
    <div class="ck-empty">
      <img src="/img/pandas/empty-requests.png" alt="Checklists unavailable" class="empty-state-img" />
      <h4 class="empty-state-text">This feature is unavailable</h4>
      <base-button type="primary" size="sm" v-if="hasPermissions('billing_view')" @click="goToBilling()" class="mt-2">Go to billing</base-button>
      <p class="ck-empty__hint" v-else>Contact a site administrator to update billing.</p>
    </div>
  </card>

  <card v-else>
    <!-- Header -->
    <!-- Tabs with the page action on the same row, same shape as the custom fields library -->
    <div class="tabs-header d-flex align-items-end mb-3">
      <tab-rail :tabs="viewTabs" :value="viewIndex" @input="switchView" class="flex-grow-1" />
      <router-link v-if="awaitingCount > 0 && hasPermissions('checklist_history_approve')"
                   :to="{ path: '/checklists/history', query: { status: 'awaiting_approval' } }"
                   class="cr-pill cr-pill--warning tabs-header__action ml-3">{{ awaitingCount }} awaiting approval</router-link>
      <base-button v-if="hasPermissions('checklists_update') && view === 'active'" type="primary" size="sm" class="ml-3 tabs-header__action" @click="createChecklistPrompt()">
        <i class="tim-icons el-icon-plus"></i><span class="d-none d-sm-inline ml-1">Create checklist</span>
      </base-button>
    </div>

    <filter-bar
      :filters="filterSchema"
      :initial-value="filterValues"
      :result-count="filteredChecklists.length"
      :loading="loading"
      saved-filters-key="checklists"
      @change="onFiltersChanged"
    >
      <template #count="{ count }">
        {{ count }} {{ view === 'archived' ? 'archived ' : '' }}checklist{{ count === 1 ? '' : 's' }}<template v-if="filteredOpenRunCount > 0"> · {{ filteredOpenRunCount }} in progress</template>
      </template>
      <template #actions>
        <base-button v-if="hasPermissions('download_data') && filteredChecklists.length > 0" icon class="btn-link text-white" @click="downloadCSV()">
          <i class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>
    </filter-bar>

    <!-- Empty state -->
    <div v-if="!loading && checklists.length === 0" class="ck-empty">
      <img src="/img/pandas/kanban.png" alt="No checklists" class="empty-state-img" />
      <h4 class="empty-state-text">{{ view === 'archived' ? 'No archived checklists' : 'No checklists yet' }}</h4>
      <p class="ck-empty__hint">{{ view === 'archived' ? 'Archived checklists show up here. Restore one to start running it again.' : 'Create a checklist for resets, opening, or closing. Staff run it from here and every step is recorded.' }}</p>
    </div>
    <div v-else-if="!loading && filteredChecklists.length === 0" class="ck-empty">
      <img src="/img/pandas/kanban.png" alt="No matches" class="empty-state-img" />
      <h4 class="empty-state-text">No checklists match these filters</h4>
      <p class="ck-empty__hint">Clear a filter or two to see more.</p>
    </div>

    <!-- List -->
    <draggable v-if="filteredChecklists.length > 0" v-model="visibleRows" tag="div" class="ck-list mt-3" handle=".ck-card__drag" :disabled="!canReorder" @end="saveOrder">
      <div v-for="item in visibleRows" :key="'C' + item.id" class="ck-card">
        <div class="ck-card__drag" v-if="canReorder" title="Drag to reorder">
          <i class="tim-icons el-icon-rank"></i>
        </div>

        <div class="ck-card__info">
          <div class="ck-card__title">
            <span class="ck-card__name text-white">{{ item.name }}</span>
            <span v-if="item.game_id && getGameName(item.game_id)" class="cr-pill">{{ getGameName(item.game_id) }}</span>
            <span v-if="item.requires_approval === 1" class="cr-pill cr-pill--info">Requires approval</span>
          </div>
          <div v-if="item.description" class="ck-card__desc">{{ item.description }}</div>
          <div v-if="getOpenRuns(item).length > 0" class="ck-card__runs">
            <base-button v-for="r in getOpenRuns(item)" :key="'R' + r.id" variant="outlined" tone="primary" @click.native="resumeRun(r)">
              <i class="tim-icons icon-refresh-02"></i> Resume
              <span class="ck-card__run-meta">{{ r.r_first_name }} {{ r.r_last_name }} · {{ getLocalTime(r.start_time) }}</span>
            </base-button>
          </div>
        </div>

        <div class="ck-card__actions">
          <template v-if="view === 'active'">
          <el-select v-if="hasSessions(item)"
                     clearable
                     size="small"
                     class="select-primary ck-card__session"
                     placeholder="Event session"
                     v-model="item.selectedGameInstance"
                     value-key="id">
            <el-option
              v-for="(g, i) in getInstances(item.game_instances)"
              class="select-primary"
              :label="getDate(g.booking_date) + ', ' + getTime(g.start_time)"
              :value="{ game_name: g.game_name, id: g.id, booking_date: g.booking_date, start_time: g.start_time}"
              :key="'G' + i"
            ></el-option>
          </el-select>
          <base-button type="primary" size="sm" class="ck-card__start" @click.native="startChecklist(item)">
            <i class="tim-icons icon-triangle-right-17 mr-1"></i> Start
          </base-button>
          </template>
          <el-dropdown v-if="hasPermissions('checklists_delete') || hasPermissions('checklists_update')" trigger="click">
            <base-button simple type="primary" link size="sm">
              <i class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-1 mb-2" v-if="view === 'active' && hasPermissions('checklists_update')" @click="editChecklist(item, $event)" href="#">
                  <i class="tim-icons icon-pencil mr-2"></i> Edit checklist
                </a>
                <a class="dropdown-item mt-1 mb-2" v-if="view === 'active' && hasPermissions('checklists_update')" @click="duplicateChecklist(item, $event)" href="#">
                  <i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate
                </a>
                <hr v-if="view === 'active' && hasPermissions('checklists_delete') && hasPermissions('checklists_update')">
                <a class="dropdown-item" v-if="view === 'active' && hasPermissions('checklists_delete')" @click="archiveChecklistPrompt(item, $event)" href="#">
                  <i class="tim-icons text-default icon-trash-simple mr-2"></i> Archive checklist
                </a>
                <a class="dropdown-item mt-1 mb-2" v-if="view === 'archived' && hasPermissions('checklists_update')" @click="restoreChecklist(item, $event)" href="#">
                  <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </draggable>
  </card>

  <base-alert-dialog
    :show="confirm.show"
    title="Archive this checklist?"
    message="It leaves this page and cannot be started. Its runs stay in history. Restore it any time from the Archived view."
    variant="warning"
    show-cancel
    confirm-text="Archive"
    confirm-variant="primary"
    @confirm="onConfirmArchive()"
    @cancel="confirm.show = false"
  />
  <base-alert-dialog
    :show="alert.show"
    :title="alert.title"
    :message="alert.message"
    :variant="alert.variant"
    @confirm="onAlertConfirm()"
    @cancel="alert.show = false"
  />
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import _ from "lodash";
import {formattedDate, time} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import draggable from 'vuedraggable'
import {Dropdown, DropdownItem, DropdownMenu} from "element-ui";
import axios from "axios";
import TabRail from '@/components/TabRail.vue';

export default {
  name: "Checklists",
  components: {
    draggable,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    TabRail,
  },
  data() {
    return {
      loading: false,
      feature_not_enabled: false,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      checklists: [],
      games: [],
      selectedGameInstance: '',
      confirm: { show: false, item: null },
      view: 'active',
      viewTabs: [{ name: 'Active', shortName: 'Active' }, { name: 'Archived', shortName: 'Archived' }],
      filterValues: { name: '', event: '', requires_approval: false, in_progress: false },
      alert: { show: false, title: '', message: '', variant: 'warning', redirect: '' }
    };
  },
  methods: {
    async duplicateChecklist(item, event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/' + item.id

      let data = {
        duplicate: true,
      }
      this.axios.put(url, data, config)
        .then(response => {
          this.$notify({ type: 'success', message: 'Checklist duplicated', icon: 'tim-icons icon-check-2' })
          this.$router.push({ path: '/checklists/edit', query: { id:  response.data }})
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.handleRequestError(err)
        })
    },
    async saveOrder(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/checklists'

      let data = {
        checklists:  this.checklists,
      }

      axios.put(url, data, config)
        .then(response => {
          this.getChecklists(1)
        }).catch((err) => {
        this.loading = false
        this.handleRequestError(err)
      })
    },
    goToBilling(){
      this.$router.push('/settings/billing')
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    archiveChecklistPrompt(item, event){
      event.preventDefault()
      this.confirm.item = item
      this.confirm.show = true
    },
    onConfirmArchive(){
      this.confirm.show = false
      if(this.confirm.item){
        this.setArchived(this.confirm.item, true)
      }
    },
    restoreChecklist(item, event){
      event.preventDefault()
      this.setArchived(item, false)
    },
    async setArchived(item, archived){
      // Archive is a flag on the checklist; runs stay in history either way.
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/' + item.id
      try {
        await axios.put(url, { archived: archived ? 1 : 0 }, config)
        this.$notify({ type: 'success', message: archived ? 'Checklist archived' : 'Checklist restored', icon: 'tim-icons icon-check-2' })
        this.getChecklists()
      } catch (err) {
        this.loading = false
        this.handleRequestError(err)
      }
    },
    editChecklist(item, event){
      event.preventDefault()
      this.$router.push({ path: '/checklists/edit', query: { id:  item.id }})
    },
    createChecklistPrompt() {
      this.$router.push('/checklists/settings')
    },
    showAlert(title, message, variant, redirect){
      this.alert.title = title
      this.alert.message = message
      this.alert.variant = variant || 'warning'
      this.alert.redirect = redirect || ''
      this.alert.show = true
    },
    onAlertConfirm(){
      this.alert.show = false
      if(this.alert.redirect){
        this.$router.push(this.alert.redirect)
      }
    },
    parseErrorMessage(err){
      const data = err && err.response ? err.response.data : null
      if(!data) return 'Something went wrong'
      if(typeof data === 'string'){
        try { return JSON.parse(data).error || data } catch (e) { return data }
      }
      return data.error || 'Something went wrong'
    },
    getOpenRuns(item){
      if(!item.open_runs) return []
      try { return JSON.parse(item.open_runs) } catch (e) { return [] }
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    resumeRun(run){
      this.$router.push({ path: '/checklists/active', query: { run: run.id }})
    },
    async startChecklist(checklist){
      // Creates the run on the server so it survives reloads and can be
      // resumed or shared. Steps are saved as they happen on the active page.
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/' + checklist.id + '/instances'
      let data = {
        game_instance_id: checklist.selectedGameInstance ? checklist.selectedGameInstance.id : null
      }
      try {
        const response = await axios.post(url, data, config)
        this.$router.push({ path: '/checklists/active', query: { run: response.data.id }})
      } catch (err) {
        this.loading = false
        console.log(err)
        const status = err && err.response ? err.response.status : 0
        if(status == 401){
          this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
        }
        else if(status == 500){
          this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
        }
        else {
          this.showAlert('Error', this.parseErrorMessage(err), 'danger')
        }
      }
    },
    getDate(d){
      return formattedDate(this.date_format, d)
    },
    getTime(d) {
      return time(this.time_format, d);
    },
    getInstances(item){
      if(!item) return []
      try { return _.orderBy(JSON.parse(item), 'booking_date', 'desc') } catch (e) { return [] }
    },
    async getGames () {
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
      this.axios.get(url, config)
        .then(response => {
          this.games = response.data
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.handleRequestError(err)
        })
    },
    getGameName(id){
      if(!id){
        return '-'
      } else if (!(_.find(this.games, { id: id }))){
        return false
      }
      return _.find(this.games, { id: id }).name
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getChecklists(no_load){
      if(no_load){
        this.loading = false
      } else {
        this.loading = true
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/start'
      if(this.view === 'archived'){
        url += '?archived=1'
      }
      this.axios.get(url, config)
        .then(response => {
          this.checklists = response.data
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          if(err && err.response && err.response.status == 500){
            this.feature_not_enabled = true
            return
          }
          this.handleRequestError(err)
        })
    },
    switchView(index){
      this.view = index === 1 ? 'archived' : 'active'
    },
    onFiltersChanged(values){
      this.filterValues = { ...this.filterValues, ...values }
    },
    escapeCSVValue(value){
      if(value === null || value === undefined) return ''
      value = String(value).replace(/"/g, '""')
      if(value.includes(',') || value.includes('\n') || value.includes('"')) value = `"${value}"`
      return value
    },
    downloadCSV(){
      let csv = 'Name,Event,Description,Requires approval,Runs in progress,Awaiting approval,Archived\n'
      this.filteredChecklists.forEach(c => {
        csv += [
          this.escapeCSVValue(c.name),
          this.escapeCSVValue(c.game_id ? this.getGameName(c.game_id) : ''),
          this.escapeCSVValue(c.description),
          this.escapeCSVValue(Number(c.requires_approval) === 1 ? 'Yes' : 'No'),
          this.escapeCSVValue(this.getOpenRuns(c).length),
          this.escapeCSVValue(Number(c.awaiting_approval_count) || 0),
          this.escapeCSVValue(Number(c.archived) === 1 ? 'Yes' : 'No')
        ].join(',') + '\n'
      })
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', this.view === 'archived' ? 'archived-checklists.csv' : 'checklists.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    hasSessions(item){
      if(!item.game_id) return false
      const list = this.getInstances(item.game_instances)
      return list.length > 0 && list[0].id !== null
    },
    handleRequestError(err, context){
      console.log(err)
      const status = err && err.response ? err.response.status : 0
      if(status == 401){
        this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
      } else if(status == 500){
        this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
      } else {
        this.showAlert('Error', this.parseErrorMessage(err), 'danger')
      }
    }
  },
  computed: {
    filteredOpenRunCount(){
      return this.filteredChecklists.reduce((n, c) => n + this.getOpenRuns(c).length, 0)
    },
    awaitingCount(){
      return this.checklists.reduce((n, c) => n + (Number(c.awaiting_approval_count) || 0), 0)
    },
    viewIndex(){
      return this.view === 'archived' ? 1 : 0
    },
    filterSchema(){
      return [
        {key: 'name', type: 'text', label: 'Name', placeholder: 'Checklist name', operatorLabel: 'contains', primary: true},
        {key: 'event', type: 'select', label: 'Event', primary: true,
          options: this.games.map(g => ({label: g.name, value: g.id}))},
        {key: 'requires_approval', type: 'toggle', label: 'Requires approval'},
        {key: 'in_progress', type: 'toggle', label: 'Has runs in progress'},
      ]
    },
    hasActiveFilters(){
      const f = this.filterValues
      return !!(f.name && f.name.trim()) || !!f.event || !!f.requires_approval || !!f.in_progress
    },
    filteredChecklists(){
      const f = this.filterValues
      const q = (f.name || '').trim().toLowerCase()
      return this.checklists.filter(c => {
        if(q && !(c.name || '').toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) return false
        if(f.event && Number(c.game_id) !== Number(f.event)) return false
        if(f.requires_approval && Number(c.requires_approval) !== 1) return false
        if(f.in_progress && this.getOpenRuns(c).length === 0) return false
        return true
      })
    },
    // Drag only makes sense on the real order: active view, no filters narrowing the list.
    canReorder(){
      return this.view === 'active' && !this.hasActiveFilters && this.hasPermissions('checklists_update')
    },
    // draggable needs a writable model. Unfiltered, it is the full list (so a drag reorders it);
    // filtered, it is a read-only slice since drag is disabled.
    visibleRows: {
      get(){
        return this.hasActiveFilters ? this.filteredChecklists : this.checklists
      },
      set(rows){
        if(!this.hasActiveFilters) this.checklists = rows
      }
    }
  },
  watch: {
    view(){
      this.getChecklists()
    }
  },
  mounted() {
    this.getGames()
    this.getChecklists()
  }
}
</script>

<style scoped>
.select-primary,
.select-primary .el-input__inner {
  touch-action: manipulation;
}
</style>
